import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styles from './financeCard.module.css';
import InfoIcon from '../../../assets/icons/info-icon.svg';

const FinanceCard = ({ icon, title, value, explanation, dataLink, code, detailData }) => {
  const [showPopover, setShowPopover] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const popRef = useRef(null);
  const triggerRef = useRef(null);
  const navigate = useNavigate();

  const hasDetails = detailData && detailData.length > 0;

  useLayoutEffect(() => {
    if (!showPopover) return;

    const updatePosition = () => {
      const pop = popRef.current;
      const trigger = triggerRef.current;
      if (!pop || !trigger) return;

      const r = trigger.getBoundingClientRect();
      const margin = 8;
      const gap = 8;
      let left = r.right + gap;
      let top = r.top + r.height / 2;

      const pw = pop.offsetWidth || 220;
      const ph = pop.offsetHeight || 1;
      top -= ph / 2;

      if (left + pw > window.innerWidth - margin) {
        left = r.left - pw - gap;
      }
      if (left < margin) {
        left = margin;
      }
      if (top < margin) {
        top = margin;
      }
      if (top + ph > window.innerHeight - margin) {
        top = window.innerHeight - ph - margin;
      }

      pop.style.left = `${left}px`;
      pop.style.top = `${top}px`;
    };

    updatePosition();
    const raf = requestAnimationFrame(() => updatePosition());

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showPopover]);

  const handleNavigateToData = () => {
    if (dataLink) {
      navigate(dataLink);
    }
  };

  const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  const groupedDetails = hasDetails
    ? detailData.reduce((acc, item) => {
        if (!acc[item.year]) acc[item.year] = { total: null, semesters: {} };
        const isYearTotal = (item.semester === null || item.semester === undefined) && (item.month === null || item.month === undefined);
        const isSemesterTotal = item.semester != null && (item.month === null || item.month === undefined);
        if (isYearTotal) {
          acc[item.year].total = item;
        } else if (isSemesterTotal) {
          if (!acc[item.year].semesters[item.semester]) {
            acc[item.year].semesters[item.semester] = { total: null, months: [] };
          }
          acc[item.year].semesters[item.semester].total = item;
        } else {
          const sem = item.semester || (item.month <= 6 ? 1 : 2);
          if (!acc[item.year].semesters[sem]) {
            acc[item.year].semesters[sem] = { total: null, months: [] };
          }
          acc[item.year].semesters[sem].months.push(item);
        }
        return acc;
      }, {})
    : {};

  return (
    <div className={styles.financeCardWrapper}>
      <div className={styles.financeCard}>
        {code && <span className={styles.code}>{code}</span>}
        {icon && <img src={icon} alt="" className={styles.icon} />}

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <h6 className={styles.title}>{title}</h6>

            {explanation && (
              <div ref={triggerRef} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <img
                  src={InfoIcon}
                  alt="Informação"
                  style={{ cursor: 'pointer', width: 14, height: 14 }}
                  onClick={() => setShowPopover((prev) => !prev)}
                />

                {showPopover &&
                  createPortal(
                    <div
                      ref={popRef}
                      className={styles.popoverPortal}
                      role="dialog"
                      aria-label="Informação do indicador"
                    >
                      <p className={styles.popoverText}>{explanation}</p>
                      {dataLink && (
                        <button
                          type="button"
                          className={styles.dataBtn}
                          onClick={handleNavigateToData}
                        >
                          Ver dados consolidados
                        </button>
                      )}
                      <button
                        type="button"
                        className={styles.closeBtn}
                        onClick={() => setShowPopover(false)}
                      >
                        Fechar
                      </button>
                    </div>,
                    document.body
                  )}
              </div>
            )}
          </div>

          <h2 className={styles.value}>{value}</h2>

          {hasDetails && (
            <button
              type="button"
              className={styles.detailToggle}
              onClick={() => setIsExpanded(prev => !prev)}
            >
              {isExpanded ? '− ocultar detalhes' : '+ detalhes por período'}
            </button>
          )}
        </div>
      </div>

      {hasDetails && isExpanded && (
        <div className={styles.detailPanel}>
          <table className={styles.detailTable}>
            <thead>
              <tr>
                <th>Período</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(groupedDetails)
                .sort((a, b) => Number(a) - Number(b))
                .map(year => (
                  <React.Fragment key={year}>
                    {groupedDetails[year].total && (
                      <tr className={styles.detailRowYear}>
                        <td>{year}</td>
                        <td>{groupedDetails[year].total.value}</td>
                      </tr>
                    )}
                    {Object.keys(groupedDetails[year].semesters)
                      .sort((a, b) => Number(a) - Number(b))
                      .map(semKey => {
                        const semData = groupedDetails[year].semesters[semKey];
                        return (
                          <React.Fragment key={`${year}-sem${semKey}`}>
                            {semData.total && (
                              <tr className={styles.detailRowSemester}>
                                <td>{Number(semKey) === 1 ? '1º Semestre' : '2º Semestre'}</td>
                                <td>{semData.total.value}</td>
                              </tr>
                            )}
                            {semData.months
                              .sort((a, b) => a.month - b.month)
                              .map(m => (
                                <tr key={`${year}-${semKey}-${m.month}`} className={styles.detailRowMonth}>
                                  <td>{monthNames[(m.month || 1) - 1]}</td>
                                  <td>{m.value}</td>
                                </tr>
                              ))}
                          </React.Fragment>
                        );
                      })}
                  </React.Fragment>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FinanceCard;
